import { FormEvent, useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { ChevronLeft } from "lucide-react"
import {
  deleteStoryEntry,
  fetchStory,
  updateStoryEntry,
  type StoryEntry,
} from "~/api/story"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"
import { useProfile } from "~/hooks/useProfile"
import { ENTITLEMENTS, hasEntitlement } from "~/utils/entitlements"

export function StoryDetailPage() {
  const { entryId } = useParams<{ entryId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { profile } = useProfile()
  const [entry, setEntry] = useState<StoryEntry | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [storyWritable, setStoryWritable] = useState(false)
  const [editTitle, setEditTitle] = useState("")
  const [editBody, setEditBody] = useState("")
  const [editNote, setEditNote] = useState("")

  const dogId = useMemo(() => {
    const raw = searchParams.get("dog_id")
    if (!raw) return undefined
    const id = Number(raw)
    return Number.isFinite(id) ? id : undefined
  }, [searchParams])

  const accountHasJournal = hasEntitlement(profile?.user.entitlements, ENTITLEMENTS.dogJournal)
  const canWrite = accountHasJournal || storyWritable

  const storyListPath = dogId ? `/story?dog_id=${dogId}` : "/story"

  useEffect(() => {
    const id = Number(entryId)
    if (!Number.isFinite(id)) {
      setError("Memory not found")
      setLoading(false)
      return
    }
    fetchStory(dogId)
      .then((res) => {
        const found = res.entries.find((item) => item.id === id) ?? null
        setEntry(found)
        setStoryWritable(Boolean(res.has_entitlement) && !res.read_only)
        if (found) {
          setEditTitle(found.title)
          setEditBody(found.body ?? "")
          setEditNote(found.user_note ?? "")
        } else {
          setError("Memory not found")
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Could not load memory"))
      .finally(() => setLoading(false))
  }, [entryId, dogId])

  async function handleDelete() {
    if (!entry || deleting || !canWrite || entry.deletable === false) return
    const linked = entry.linked_dog_names?.length ? entry.linked_dog_names : []
    const confirmMessage =
      linked.length > 1
        ? `Remove this shared memory from ${linked.join(", ")}’s Stories? This cannot be undone.`
        : "Remove this memory from the Story? This cannot be undone."
    if (!window.confirm(confirmMessage)) return
    setDeleting(true)
    try {
      await deleteStoryEntry(entry.id, dogId)
      navigate(storyListPath, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete memory")
      setDeleting(false)
    }
  }

  async function handleSaveEdit(event: FormEvent) {
    event.preventDefault()
    if (!entry || saving || !canWrite) return
    setSaving(true)
    setError(null)
    try {
      const isConversation = entry.entry_type === "conversation"
      const result = await updateStoryEntry(
        entry.id,
        isConversation
          ? { user_note: editNote.trim() || undefined }
          : {
              title: editTitle.trim(),
              body: editBody.trim() || undefined,
              user_note: editNote.trim() || undefined,
            },
        dogId,
      )
      setEntry(result.entry)
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save changes")
    } finally {
      setSaving(false)
    }
  }

  const displayNote =
    entry?.user_note &&
    !entry.user_note.startsWith("/images/") &&
    !entry.user_note.startsWith("/rails/") &&
    !entry.user_note.startsWith("/share-assets/") &&
    !entry.user_note.includes("/rails/active_storage/")
      ? entry.user_note
      : null

  const showEdit = canWrite && entry && (entry.editable ?? (!entry.automatic && entry.entry_type !== "milestone"))

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
        <header className="mb-4 flex items-center gap-3">
          <Link
            to={storyListPath}
            aria-label="Back to Story"
            className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
          >
            <ChevronLeft size={20} aria-hidden />
          </Link>
          <h1 className="flex-1 text-lg font-semibold text-[var(--color-text-primary)]">Memory</h1>
        </header>

        {loading && <p className="text-[var(--color-text-secondary)]">Loading…</p>}
        {error && (
          <p className="text-sm text-red-700" role="alert">
            {error}
          </p>
        )}
        {entry && (
          <article className="rounded-xl border border-[var(--color-divider)] bg-[var(--color-bg-primary)] p-5">
            <p className="text-xs uppercase tracking-wide text-[var(--color-text-tertiary)]">
              {entry.entry_type}
              {entry.automatic ? " · automatic" : ""}
            </p>
            {!editing ? (
              <>
                <h2 className="mt-1 text-xl font-semibold text-[var(--color-text-primary)]">
                  {entry.title}
                </h2>
                <time className="mt-1 block text-sm text-[var(--color-text-secondary)]">
                  {new Date(entry.occurred_at).toLocaleString()}
                </time>
                {entry.image_url && (
                  <img
                    src={entry.image_url}
                    alt={entry.title}
                    className="mt-4 max-h-80 w-full rounded-lg object-cover"
                  />
                )}
                {entry.body && (
                  <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-[var(--color-text-primary)]">
                    {entry.body}
                  </p>
                )}
                {displayNote && (
                  <div className="mt-4 rounded-lg bg-[var(--color-surface-card)] p-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-tertiary)]">
                      Your note
                    </p>
                    <p className="mt-1 text-sm text-[var(--color-text-primary)]">{displayNote}</p>
                  </div>
                )}
                <div className="mt-6 flex flex-wrap gap-4">
                  {showEdit && (
                    <button
                      type="button"
                      onClick={() => setEditing(true)}
                      className="text-sm font-medium text-[var(--color-text-primary)] underline"
                    >
                      Edit
                    </button>
                  )}
                  {canWrite && entry.deletable !== false && !entry.automatic && (
                    <button
                      type="button"
                      onClick={() => void handleDelete()}
                      disabled={deleting}
                      className="text-sm font-medium text-red-700 underline disabled:opacity-50"
                    >
                      {deleting ? "Removing…" : "Delete memory"}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <form onSubmit={(event) => void handleSaveEdit(event)} className="mt-3 space-y-3">
                {entry.entry_type !== "conversation" && (
                  <>
                    <label className="block text-sm font-medium text-[var(--color-text-primary)]">
                      Title
                      <input
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        required
                        className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-white px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block text-sm font-medium text-[var(--color-text-primary)]">
                      Details
                      <textarea
                        rows={5}
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-white px-3 py-2 text-sm"
                      />
                    </label>
                  </>
                )}
                <label className="block text-sm font-medium text-[var(--color-text-primary)]">
                  {entry.entry_type === "conversation" ? "Your note" : "Note"}
                  <textarea
                    rows={3}
                    value={editNote}
                    onChange={(e) => setEditNote(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-white px-3 py-2 text-sm"
                  />
                </label>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-lg bg-[var(--color-brand-primary)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(false)
                      setEditTitle(entry.title)
                      setEditBody(entry.body ?? "")
                      setEditNote(entry.user_note ?? "")
                    }}
                    className="rounded-lg border border-[var(--color-divider)] px-4 py-2 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </article>
        )}
      </LoggedInScreenCard>
    </PageShell>
  )
}
